import React, { useEffect, useState } from 'react';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import FechamentoDeFolha from 'Interface/FechamentosDeFolha';
import OrdemDeServico from 'Interface/OS/OrdensDeServico';
import VinculoEmpregaticio from 'Interface/VinculosEmpregaticios';
import {
  useAdicionarFechamentoDeFolhaMutation,
  useEditarFechamentoDeFolhaMutation,
  useRecuperarFechamentosDeFolhaQuery
} from 'View/Service/Default/FechamentosDeFolha';
import { useRecuperarOrdensDeServicoQuery } from 'View/Service/Default/OrdensDeServico';

interface Props {
  dados: VinculoEmpregaticio;
  inicio: string;
  termino: string;
}

const UploadLista = ({ dados, inicio, termino }: Props): React.ReactElement => {
  const toast = useToast();
  const [adicionarFolha] = useAdicionarFechamentoDeFolhaMutation();
  const [editarFolha] = useEditarFechamentoDeFolhaMutation();
  const [estado, setEstado] = useState({
    comissao: 0,
    ajuda_de_custo: 0,
    premio_de_metas_vendedores: 0,
    premio_de_metas_geral: 0,
    vale_transporte: 0,
    adiantamento: 0,
    farmacia: 0,
    medico: 0,
    odonto: 0,
    consignado: 0,
    descontos: 0,
    observacoes: '',
  });

  const {
    data: { fechamentos_de_folha: [folha] } = {
      fechamentos_de_folha: [{} as FechamentoDeFolha],
    },
    isFetching: buscandoFolha,
  } = useRecuperarFechamentosDeFolhaQuery({
    options: { usuario_id: dados.usuario_id, inicio: inicio, termino: termino },
  });

  useEffect(() => {
    if (folha) {
      setEstado({
        comissao: folha.valor,
        ajuda_de_custo: folha.ajuda_de_custo as number,
        premio_de_metas_vendedores: folha.premio_de_metas_vendedores as number,
        premio_de_metas_geral: folha.premio_de_metas_geral as number,
        vale_transporte: folha.vale_transporte as number,
        adiantamento: folha.adiantamento as number,
        farmacia: folha.farmacia as number,
        medico: folha.medico as number,
        odonto: folha.odonto as number,
        consignado: folha.consignado as number,
        descontos: folha.descontos as number,
        observacoes: folha.observacoes as string,
      });
    }
  }, [folha]);

  const {
    data: { ordens_de_servico: [ordem] } = {
      ordens_de_servico: [{} as OrdemDeServico],
    },
    isFetching: buscandoOrdem,
  } = useRecuperarOrdensDeServicoQuery(
    {
      finder: 'pontuacaoDoTecnicoEntreDatas',
      options: {
        email: dados.usuario?.email,
        inicio: inicio,
        termino: termino,
      },
    },
    { skip: !inicio || !termino }
  );

  useEffect(() => {
    if (ordem.pontuacao_tecnico && !estado.comissao) {
      setEstado({
        ...estado,
        comissao: new Number(ordem.pontuacao_tecnico).toFixed(2),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordem.pontuacao_tecnico]);

  const enviar = async (): Promise<void> => {
    try {
      if (folha) {
        await editarFolha({
          ...estado,
          valor: estado.comissao,
          id: folha.id,
        }).unwrap();
      } else {
        await adicionarFolha({
          ...estado,
          valor: estado.comissao,
          inicio,
          termino,
          vinculo_id: dados.id,
        }).unwrap();
      }

      toast({
        conteudo: 'Adicionado com sucesso!',
        tema: 'success',
      });
    } catch (erro) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  return (
    <React.Fragment>
      <tr>
        <td className="text-nowrap">{dados.empresa?.razao_social}</td>
        <td className="text-nowrap">{dados.usuario?.nome}</td>
        <td className="text-nowrap">{dados.funcao?.nome}</td>
        <td className="text-nowrap">{dados.salario.moeda()}</td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="comissao"
            value={estado.comissao}
            onChange={(e) =>
              setEstado({
                ...estado,
                comissao: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="ajuda_de_custo"
            value={estado.ajuda_de_custo}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                ajuda_de_custo: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="premio_de_metas_vendedores"
            value={estado.premio_de_metas_vendedores}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                premio_de_metas_vendedores: e.currentTarget
                  .value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="premio_de_metas_geral"
            value={estado.premio_de_metas_geral}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                premio_de_metas_geral: e.currentTarget
                  .value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="vale_transporte"
            value={estado.vale_transporte}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                vale_transporte: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="adiantamento"
            value={estado.adiantamento}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                adiantamento: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="farmacia"
            value={estado.farmacia}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                farmacia: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="medico"
            value={estado.medico}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                medico: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="odonto"
            value={estado.odonto}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                odonto: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="consignado"
            value={estado.consignado}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                consignado: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="number"
            step={0.01}
            min={0}
            name="descontos"
            value={estado.descontos}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                descontos: e.currentTarget.value as unknown as number,
              })
            }
          />
        </td>
        <td className="text-nowrap">
          <input
            type="text"
            name="observacao"
            value={estado.observacoes}
            disabled={buscandoFolha || buscandoOrdem}
            onChange={(e) =>
              setEstado({
                ...estado,
                observacoes: e.currentTarget.value,
              })
            }
          />
        </td>
        <td>
          <Botao sm tema="success" icone="send" onClick={enviar} />
        </td>
      </tr>
    </React.Fragment>
  );
};

export default UploadLista;
